/**
 * 指挥访问口令 / 公告 / 移动端断点
 */
import { useEffect, useRef, useState } from 'react';
import { authenticateCommandAccess, isCommandPasswordConfigured } from '../utils/commandAuth';
import {
  fetchPublicAnnouncementState,
  closePublicAnnouncement
} from '../utils/dashboard';

const CONFIG_CHECK_TIMEOUT_MS = 8000;
const VERIFY_TIMEOUT_MS = 15000;

function withTimeout(promise, ms, label) {
  let timer;
  const timeout = new Promise((_, reject) => {
    timer = setTimeout(() => reject(new Error(label || '请求超时')), ms);
  });
  return Promise.race([promise, timeout]).finally(() => clearTimeout(timer));
}

export default function useDashboardCommandGate({ isLoggedIn, authed, setAuthed }) {
  const [inputPwd, setInputPwd] = useState('');
  const [pwdError, setPwdError] = useState('');
  /** 仅「查询是否已配置」阶段的整页加载；验证密码时用 verifying，避免输入框被藏掉 */
  const [loadingPwd, setLoadingPwd] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [commandPwd, setCommandPwd] = useState('');
  const [announcement, setAnnouncement] = useState(null);
  const [showAnnouncement, setShowAnnouncement] = useState(true);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 900);
  const checkSeqRef = useRef(0);

  useEffect(() => {
    if (authed || !isLoggedIn) return undefined;
    const seq = ++checkSeqRef.current;
    setLoadingPwd(true);
    setPwdError('');
    withTimeout(isCommandPasswordConfigured(), CONFIG_CHECK_TIMEOUT_MS, '查询指挥口令状态超时')
      .then((configured) => {
        if (seq !== checkSeqRef.current) return;
        setCommandPwd(configured ? 'configured' : '');
        if (!configured) {
          // 未配置时仍允许尝试验证（后端会返回明确错误）；不阻断输入框
          setPwdError('尚未检测到指挥访问密码配置，若无法进入请联系管理员');
        }
      })
      .catch((err) => {
        if (seq !== checkSeqRef.current) return;
        // 查询失败（含非管理员 403 旧接口）不阻断：照常显示输入框
        setCommandPwd('configured');
        console.warn('指挥口令配置查询失败，仍显示验证框:', err?.message || err);
      })
      .finally(() => {
        if (seq === checkSeqRef.current) setLoadingPwd(false);
      });
    return () => { checkSeqRef.current += 1; };
  }, [authed, isLoggedIn]);

  const checkPwd = async () => {
    if (verifying) return;
    setVerifying(true);
    setPwdError('');
    try {
      const result = await withTimeout(
        authenticateCommandAccess(inputPwd),
        VERIFY_TIMEOUT_MS,
        '验证超时，请检查网络后重试'
      );
      if (!result.ok) {
        setPwdError(result.error || '密码错误，请重试');
        return;
      }
      try {
        localStorage.setItem('command_authed', '1');
      } catch (_) { /* ignore */ }
      setAuthed(true);
      setPwdError('');
      setInputPwd('');
    } catch (err) {
      setPwdError(err?.message || '验证失败，请重试');
    } finally {
      setVerifying(false);
    }
  };

  useEffect(() => {
    const fetchAnnouncement = async () => {
      const { announcement: ann, show } = await fetchPublicAnnouncementState();
      setAnnouncement(ann);
      setShowAnnouncement(show);
    };
    fetchAnnouncement();
    const interval = setInterval(fetchAnnouncement, 60000);
    return () => clearInterval(interval);
  }, []);

  const closeAnnouncement = () => {
    closePublicAnnouncement(announcement);
    setShowAnnouncement(false);
  };

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 900);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return {
    inputPwd,
    setInputPwd,
    pwdError,
    setPwdError,
    loadingPwd,
    verifying,
    commandPwd,
    announcement,
    showAnnouncement,
    isMobile,
    checkPwd,
    closeAnnouncement
  };
}
